import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, CheckCircle, ArrowRight } from 'lucide-react';

export const RegisterModal = ({ open, onOpenChange }) => {
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    company: '',
    designation: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleStep1 = (e) => {
    e.preventDefault();
    if (formData.email && formData.company) {
      setStep(2);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await register(formData);
      toast.success('Welcome! You can now view candidate portfolios.');
      onOpenChange(false);
      setFormData({ name: '', email: '', password: '', phone: '', company: '', designation: '' });
      setStep(1);
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setStep(1);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-hfs-navy">
            {step === 1 ? 'View Candidate Portfolios' : 'Complete Your Profile'}
          </DialogTitle>
          <DialogDescription>
            {step === 1 
              ? 'Enter your work email to access detailed candidate profiles and portfolios.'
              : 'Just a few more details to complete your registration.'
            }
          </DialogDescription>
        </DialogHeader>

        {/* Benefits */}
        {step === 1 && (
          <div className="flex flex-col gap-2 py-3 border-y border-slate-100 my-2">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span>Access detailed candidate portfolios</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span>View skills, experience & contact info</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span>No recruiter fees • Free forever</span>
            </div>
          </div>
        )}
        
        {step === 1 ? (
          <form onSubmit={handleStep1} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label htmlFor="email">Work Email *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@company.com"
                required
                className="h-11"
                data-testid="register-email-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="company">Company *</Label>
              <Input
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder="Your Company Name"
                required
                className="h-11"
                data-testid="register-company-input"
              />
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-hfs-navy hover:bg-hfs-navy-dark text-white h-11"
              data-testid="register-continue-btn"
            >
              Continue
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            
            <p className="text-center text-xs text-slate-500">
              Already have an account?{' '}
              <a href="/login" className="text-hfs-navy hover:underline font-medium">
                Login here
              </a>
            </p>
          </form>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  required
                  data-testid="register-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+91 98765 43210"
                  data-testid="register-phone-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="designation">Designation</Label>
                <Input
                  id="designation"
                  name="designation"
                  value={formData.designation}
                  onChange={handleChange}
                  placeholder="HR Manager"
                  data-testid="register-designation-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password *</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  data-testid="register-password-input"
                />
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button 
                type="button"
                variant="outline"
                onClick={() => setStep(1)}
                className="flex-1 h-11"
              >
                Back
              </Button>
              <Button 
                type="submit" 
                className="flex-1 bg-hfs-navy hover:bg-hfs-navy-dark text-white h-11"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'View Portfolios'
                )}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
